import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { faker } from "@faker-js/faker";
import React from "react";

import { ApiTreatmentPlanTemplateFieldFieldTypeChoices } from "@api/graphql/types-and-hooks";
import { TemplateFieldState } from "@pages/Templates/components/TreatmentPlanTemplateEditor/TreatmentPlanTemplateEditor";
import { TreatmentPlanTemplateEditorAddButton } from "@pages/Templates/components/TreatmentPlanTemplateEditor/TreatmentPlanTemplateEditorAddButton";
import { TreatmentPlanTemplateEditorField } from "@pages/Templates/components/TreatmentPlanTemplateEditor/TreatmentPlanTemplateEditorField";

interface TreatmentPlanTemplateEditorFieldListProps {
  fields: TemplateFieldState[];
  onUpdateFields: (fields: TemplateFieldState[]) => void;
  readonly: boolean;
  addLabel: string;
  fieldType: string;
}

export const TreatmentPlanTemplateEditorFieldList: React.FC<TreatmentPlanTemplateEditorFieldListProps> = ({
  fields,
  onUpdateFields,
  readonly,
  addLabel,
  fieldType,
}) => {
  const fieldSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleFieldDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = fields.findIndex((f) => f.id === active.id);
      const newIndex = fields.findIndex((f) => f.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = arrayMove(fields, oldIndex, newIndex).map((f, i) => ({ ...f, rankOrder: i + 1 }));
        onUpdateFields(reordered);
      }
    }
  };

  const handleUpdateField = (updatedField: TemplateFieldState) => {
    onUpdateFields(fields.map((f) => (f.id === updatedField.id ? updatedField : f)));
  };

  const handleRemoveField = (fieldId: string) => {
    onUpdateFields(fields.filter((f) => f.id !== fieldId).map((f, i) => ({ ...f, rankOrder: i + 1 })));
  };

  const handleAddField = () => {
    const newField: TemplateFieldState = {
      id: `temp-${faker.string.uuid()}`,
      fieldIdentifier: "",
      title: "",
      fieldType: ApiTreatmentPlanTemplateFieldFieldTypeChoices.SimpleText,
      rankOrder: fields.length + 1,
      isRequired: false,
      isEditable: true,
      isEnabled: true,
      type: fieldType,
      reportGoalField: false,
      reportTargetField: false,
    };
    onUpdateFields([...fields, newField]);
  };

  return (
    <div className="mb-4">
      <DndContext sensors={fieldSensors} collisionDetection={closestCenter} onDragEnd={handleFieldDragEnd}>
        <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {fields.map((field) => (
              <TreatmentPlanTemplateEditorField
                key={field.id}
                field={field}
                onUpdate={handleUpdateField}
                onRemove={() => handleRemoveField(field.id)}
                readonly={readonly || !field.isEditable}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {!readonly && <TreatmentPlanTemplateEditorAddButton label={addLabel} onClick={handleAddField} />}
    </div>
  );
};
